import React, { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api } from "../lib/api";
import { Star, CheckCircle2, ThumbsUp, Share2, MessageCircle, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Patient-side review card.
 *
 * Renders on a completed case detail page for the patient only. Shows a
 * thank-you state if a review already exists, otherwise a 5-star + comment
 * form. Submitting calls POST /consultations/{cid}/review.
 */
export default function ReviewCard({ consultationId, doctorName }) {
  const [existing, setExisting] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [publish, setPublish] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/consultations/${consultationId}/review`);
        if (r.data?.id) {
          setExisting(r.data);
          setRating(r.data.rating);
          setText(r.data.text || "");
        }
      } catch (e) { /* 404/403 silently */ }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultationId]);

  // If the patient followed the email/WhatsApp deep-link with #review, auto-scroll into view
  useEffect(() => {
    if (window.location.hash === "#review" && !loading) {
      setTimeout(() => {
        document.getElementById("review-card")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    }
  }, [loading]);

  const submit = async () => {
    if (rating < 1) { toast.error("Please choose a rating from 1 to 5 stars"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/consultations/${consultationId}/review`, {
        rating, text: text.trim() || null, publish_publicly: publish,
      });
      setExisting(r.data);
      toast.success("Thank you! Your review has been recorded.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit review");
    } finally { setBusy(false); }
  };

  if (loading) return null;

  if (existing) {
    const happy = (existing.rating || 0) >= 4;
    return (
      <Card id="review-card" className="p-6 border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-teal-50/40" data-testid="review-card-done">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
            <CheckCircle2 className="w-5 h-5"/>
          </span>
          <div>
            <div className="font-heading text-lg font-semibold text-slate-900">Thanks for your review!</div>
            <div className="text-xs text-slate-500">Your honest feedback helps other patients trust us.</div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Stars value={existing.rating} readOnly/>
          <span className="text-sm font-semibold text-slate-700">{existing.rating}.0 / 5</span>
        </div>
        {existing.text && (
          <blockquote className="mt-3 rounded-lg border border-emerald-200 bg-white p-3 text-sm text-slate-700 italic">
            &ldquo;{existing.text}&rdquo;
          </blockquote>
        )}

        {/* Referral boost — happy patients get a one-tap "Share with family"
            widget right after leaving a review. Non-blocking, dismissible via
            "Edit my review" below. */}
        {happy && <ReferralShare doctorName={doctorName}/>}

        <Button
          variant="outline" size="sm" className="mt-4"
          onClick={() => { setExisting(null); setHover(0); }}
          data-testid="review-edit-btn"
        >
          Edit my review
        </Button>
      </Card>
    );
  }

  return (
    <Card id="review-card" className="p-6 border-amber-200 bg-gradient-to-br from-amber-50/60 to-orange-50/30" data-testid="review-card">
      <div className="flex items-start gap-3 flex-wrap">
        <span className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 grid place-items-center shrink-0">
          <ThumbsUp className="w-5 h-5"/>
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-lg font-semibold text-slate-900">How was your experience?</div>
          <div className="text-sm text-slate-600">
            Rate the second opinion you received{doctorName ? ` from ${doctorName}` : ""}. Takes about 30 seconds.
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-2" onMouseLeave={() => setHover(0)}>
        <Stars value={hover || rating} onPick={setRating} onHover={setHover}/>
        {rating > 0 && (
          <span className="text-sm font-semibold text-slate-700 ml-2" data-testid="review-rating-value">{rating}.0 / 5</span>
        )}
      </div>

      <div className="mt-4">
        <Textarea
          rows={3}
          placeholder="Optional — share what worked well or anything we could improve (will be shown anonymously on our site)…"
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 1000))}
          data-testid="review-text"
        />
        <div className="text-[10px] text-slate-400 text-right mt-0.5">{text.length}/1000</div>
      </div>

      <label className="mt-3 flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
        <input
          type="checkbox"
          checked={publish}
          onChange={(e) => setPublish(e.target.checked)}
          className="mt-1"
          data-testid="review-publish-checkbox"
        />
        <span>
          Show my review on the public NeuroOpinion site (your name will appear only as initials, e.g. <span className="font-mono text-slate-500">R.K.</span>).
        </span>
      </label>

      <div className="mt-4 flex justify-end">
        <Button
          onClick={submit}
          disabled={busy || rating < 1}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="review-submit-btn"
        >
          {busy ? "Submitting…" : "Submit review"}
        </Button>
      </div>
    </Card>
  );
}

function Stars({ value, onPick, onHover, readOnly }) {
  return (
    <div className="flex items-center gap-1" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onMouseEnter={() => onHover?.(n)}
          onClick={() => onPick?.(n)}
          className={`w-9 h-9 rounded-md grid place-items-center transition-all ${
            readOnly ? "cursor-default" : "hover:scale-110 cursor-pointer"
          }`}
          data-testid={`review-star-${n}`}
        >
          <Star
            className={`w-7 h-7 ${
              n <= (value || 0)
                ? "text-amber-500 fill-amber-500"
                : "text-slate-300"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Referral boost — shown after a 4/5-star review. Pulls the patient's real
// referral code + share text from /api/patients/me/referral so the shared
// link carries `?ref={code}` and unlocks the ₹500 mutual credit on first
// paid case. One-tap WhatsApp / native / copy — every action fires GA4
// `referral_shared` for full funnel tracking.
// ---------------------------------------------------------------------------
function ReferralShare({ doctorName }) {
  const [copied, setCopied] = useState(false);
  const [ref, setRef] = useState(null);

  useEffect(() => {
    // Fetch the patient's own referral card — includes code, share_text,
    // link and reward_rupees so the copy is always fresh. Non-blocking:
    // widget still works with a generic message if the fetch fails.
    api.get("/patients/me/referral")
      .then((r) => setRef(r.data))
      .catch(() => setRef(null));
  }, []);

  const rewardLine = ref?.reward_rupees
    ? `we BOTH get ₹${ref.reward_rupees} off your first case.`
    : "";
  const shareUrl = ref?.link || `${window.location.origin}/?ref=review`;
  const codeLine = ref?.code ? ` Use my code *${ref.code}* and ${rewardLine}` : "";
  const message = doctorName
    ? `I just got a great second opinion from ${doctorName} on NeuroOpinion India — verified NIMHANS/AIIMS neuro specialists, written report in 24–48 hrs.${codeLine}`
    : `I just got a great second opinion on NeuroOpinion India — verified NIMHANS/AIIMS neuro specialists, written report in 24–48 hrs.${codeLine}`;
  const fullText = ref?.share_text || `${message} ${shareUrl}`;

  const shareWA = () => {
    try { window.gtag?.("event", "referral_shared", { channel: "whatsapp", source: "review_boost", code: ref?.code }); } catch (_e) { /* gtag optional */ }
    window.open(`https://wa.me/?text=${encodeURIComponent(fullText)}`, "_blank");
  };
  const shareNative = async () => {
    try { window.gtag?.("event", "referral_shared", { channel: "native", source: "review_boost", code: ref?.code }); } catch (_e) { /* gtag optional */ }
    if (navigator.share) {
      try {
        await navigator.share({ title: "NeuroOpinion India", text: message, url: shareUrl });
      } catch (_e) { /* user dismissed */ }
    } else {
      copyLink();
    }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      try { window.gtag?.("event", "referral_shared", { channel: "copy", source: "review_boost", code: ref?.code }); } catch (_e) { /* gtag optional */ }
      toast.success("Copied — share it with anyone");
      setTimeout(() => setCopied(false), 2500);
    } catch (_e) { toast.error("Could not copy — try the WhatsApp button"); }
  };

  return (
    <div
      className="mt-5 rounded-xl border border-emerald-300 bg-gradient-to-br from-white to-emerald-50/60 p-4"
      data-testid="review-referral-boost"
    >
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-full bg-emerald-500 text-white grid place-items-center shrink-0">
          <Share2 className="w-4 h-4"/>
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-heading font-semibold text-slate-900 text-sm">
            {ref?.reward_rupees
              ? `Refer a friend, you both get ₹${ref.reward_rupees} off`
              : "Know someone else facing a neuro concern?"}
          </div>
          <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">
            {ref?.code
              ? <>Share your code <span className="font-mono font-bold text-emerald-700">{ref.code}</span> — they get ₹{ref.reward_rupees} off, you earn ₹{ref.reward_rupees} credit on their first paid case.</>
              : "Share NeuroOpinion with your family or friends — one message could save them months of confusion."}
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          onClick={shareWA}
          size="sm"
          className="h-9 bg-green-600 hover:bg-green-700 text-white gap-1.5"
          data-testid="referral-share-wa"
        >
          <MessageCircle className="w-4 h-4"/> Share on WhatsApp
        </Button>
        {typeof navigator !== "undefined" && navigator.share && (
          <Button
            onClick={shareNative}
            size="sm"
            variant="outline"
            className="h-9 gap-1.5"
            data-testid="referral-share-native"
          >
            <Share2 className="w-4 h-4"/> More apps
          </Button>
        )}
        <Button
          onClick={copyLink}
          size="sm"
          variant="outline"
          className="h-9 gap-1.5"
          data-testid="referral-share-copy"
        >
          {copied ? <Check className="w-4 h-4 text-emerald-600"/> : <Copy className="w-4 h-4"/>}
          {copied ? "Copied" : "Copy link"}
        </Button>
      </div>
      {ref?.stats?.paid_referrals > 0 && (
        <div className="mt-3 text-[11px] text-slate-500" data-testid="referral-stats">
          You&apos;ve earned ₹{ref.stats.total_earned} from {ref.stats.paid_referrals} paid referral{ref.stats.paid_referrals !== 1 ? "s" : ""} so far. 🎉
        </div>
      )}
    </div>
  );
}
